import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useSupabaseMetrics } from "@/hooks/useSupabaseMetrics";
import { useSupabaseRealtime } from "@/hooks/useSupabaseRealtime";
import { SupabasePerformance } from './SupabasePerformance';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  BarChart,
  Bar
} from 'recharts';
import { Database, Users, HardDrive, Cpu, Globe, Shield, Box } from 'lucide-react';

interface StorageAnalytics {
  total_size?: number;
  files_count?: number;
}

interface EdgeFunctionMetrics {
  avg_execution_time?: number;
  error_rate?: number;
}

export const SupabaseMetrics = () => {
  const { data: metricsData } = useSupabaseMetrics();
  useSupabaseRealtime();

  const lastMetric = metricsData?.[0];

  if (!lastMetric) {
    return null;
  }

  // Calculate auth metrics
  const authMetrics = {
    totalSignups: lastMetric.auth_signups || 0,
    totalLogins: lastMetric.auth_logins || 0,
    failedAttempts: lastMetric.auth_failures || 0,
    successRate: lastMetric.auth_logins && (lastMetric.auth_logins + lastMetric.auth_failures) > 0
      ? ((lastMetric.auth_logins / (lastMetric.auth_logins + lastMetric.auth_failures)) * 100).toFixed(1)
      : 0
  };

  // Format storage metrics safely
  const storageAnalytics = (lastMetric.storage_analytics || {}) as StorageAnalytics;
  const storageMetrics = {
    totalSize: (storageAnalytics.total_size || 0) / 1024 / 1024, // Convert to MB
    filesCount: storageAnalytics.files_count || 0
  };

  // Format bucket usage data safely
  const bucketUsage = lastMetric.storage_bucket_usage || {};
  const bucketData = Object.entries(bucketUsage).map(([bucket, size]) => ({
    bucket,
    size: Number(size) / 1024 / 1024 // Convert to MB
  }));

  // Format regional latency data safely
  const regionalLatency = lastMetric.regional_latency || {};
  const latencyData = Object.entries(regionalLatency).map(([region, latency]) => ({
    region,
    latency: Number(latency)
  }));

  // Format edge function metrics
  const edgeMetrics = (lastMetric.edge_function_details || {}) as EdgeFunctionMetrics;
  const edgeFunctionData = {
    totalInvocations: lastMetric.edge_function_invocations || 0,
    avgExecutionTime: edgeMetrics.avg_execution_time || 0,
    errorRate: edgeMetrics.error_rate || 0
  };

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Conexões Ativas</CardTitle>
            <Database className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {lastMetric.active_connections || 0}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Auth Events</CardTitle>
            <Shield className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {authMetrics.totalSignups} signups
            </div>
            <p className="text-xs text-muted-foreground">
              Taxa de Sucesso: {authMetrics.successRate}%
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Storage Usage</CardTitle>
            <HardDrive className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {storageMetrics.totalSize.toFixed(2)} MB
            </div>
            <p className="text-xs text-muted-foreground">
              {storageMetrics.filesCount} arquivos
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Edge Functions</CardTitle>
            <Cpu className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {edgeFunctionData.totalInvocations}
            </div>
            <p className="text-xs text-muted-foreground">
              Tempo médio: {edgeFunctionData.avgExecutionTime}ms
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Latência Regional</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={latencyData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="region" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="latency" fill="#8884d8" name="Latência (ms)" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Uso por Bucket</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={bucketData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="bucket" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="size" fill="#82ca9d" name="Tamanho (MB)" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      <SupabasePerformance />
    </div>
  );
};